import { cn } from "@/lib/cn";
import { shimmer, skeletonSurface } from "@/lib/shimmer";

/**
 * Placeholder for the hero podium (1st / 2nd / 3rd). The center card is
 * taller and lifted so the final layout swap has no vertical jump.
 */
export function PodiumSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "grid grid-cols-3 items-end gap-4",
        className,
      )}
      aria-hidden="true"
    >
      <PodiumCard height={176} medalColor="silver" />
      <PodiumCard height={220} medalColor="gold" elevated />
      <PodiumCard height={160} medalColor="bronze" />
    </div>
  );
}

function PodiumCard({
  height,
  medalColor,
  elevated = false,
}: {
  height: number;
  medalColor: "gold" | "silver" | "bronze";
  elevated?: boolean;
}) {
  const glowClass =
    medalColor === "gold"
      ? "shadow-[0_0_48px_-12px_rgba(251,191,36,0.45)] ring-1 ring-amber-300/20"
      : medalColor === "silver"
        ? "shadow-[0_0_32px_-16px_rgba(203,213,225,0.35)] ring-1 ring-slate-200/10"
        : "shadow-[0_0_32px_-16px_rgba(217,119,6,0.35)] ring-1 ring-amber-600/15";

  return (
    <div
      className={cn(
        "relative flex flex-col items-center rounded-[20px] border border-white/5 p-5",
        skeletonSurface,
        shimmer,
        glowClass,
        elevated && "-translate-y-4",
      )}
      style={{ height }}
    >
      {/* Medal circle */}
      <div
        className={cn(
          "mb-3 h-10 w-10 rounded-full",
          medalColor === "gold"
            ? "bg-amber-300/20 ring-2 ring-amber-300/30"
            : medalColor === "silver"
              ? "bg-slate-200/15 ring-2 ring-slate-200/25"
              : "bg-amber-700/20 ring-2 ring-amber-600/25",
        )}
      />
      {/* Avatar */}
      <div className="mb-3 h-14 w-14 rounded-full bg-white/5 ring-1 ring-white/10" />
      {/* Name line */}
      <div className="mb-2 h-3 w-20 rounded bg-white/5" />
      {/* Team line */}
      <div className="mb-4 h-2 w-14 rounded bg-white/[0.04]" />
      {/* Score */}
      <div className="mt-auto h-5 w-16 rounded bg-white/5" />
    </div>
  );
}
